import { Component, ElementRef, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { Destroyer } from 'src/app/core/abstract/destroyer';
import { Category } from 'src/app/core/models/categories.models';
import { StringTMap } from 'src/app/core/models/common.models';
import { DBCompany } from 'src/app/core/models/companies.models';
import { Idea, IdeaCustomFieldValue } from 'src/app/core/models/idea.models';
import { CompanyUser, PortalUser } from 'src/app/core/models/users-models';
import { AttachEvent, AttachService } from 'src/app/core/services/backend/attaches-backend.service';
import { UserPermissionsService } from 'src/app/core/services/user-permissions.service';
import { CategoriesStoreService } from 'src/app/core/store/services/categories-store.service';
import { CompaniesStoreService } from 'src/app/core/store/services/companies-store.service';
import { EntityFactory } from 'src/app/core/store/services/entity.factory';
import { IdeasStoreService } from 'src/app/core/store/services/ideas-store.service';
import { ImportancesStoreService } from 'src/app/core/store/services/importances-store.service';
import { PortalStoreService } from 'src/app/core/store/services/portal-store.service';
import { getFormValidationErrors } from 'src/app/core/utils/form-utils';
import { CustomFieldsFormsService } from 'src/app/shared/services/custom-fields-forms.service';

export type CreateIdeaDialogInput = {
  categoryId?: string;
  importanceId?: string;
};

@Component({
  templateUrl: './idea-create.component.html',
  styleUrls: ['./idea-create.component.scss'],
})
export class IdeaCreateDialogComponent extends Destroyer implements OnInit {
  @ViewChild('scrollConteiner') public scrollConteinerRef: ElementRef;

  public form: UntypedFormGroup;
  public isSending = false;
  public isSubmitted = false;
  public newIdea: Readonly<Idea>;
  public newCategory?: Readonly<Category>;

  private selectedCategoryId?: string;
  private companiesMap: StringTMap<DBCompany> = {};

  constructor(
    public ups: UserPermissionsService,
    public portalStore: PortalStoreService,

    private router: Router,
    private attachService: AttachService,
    private entityFactory: EntityFactory,
    private ideasStore: IdeasStoreService,
    private companiesStore: CompaniesStoreService,
    private categoriesStore: CategoriesStoreService,
    private customFieldsForms: CustomFieldsFormsService,
    private importancesStore: ImportancesStoreService,
    private dialogRef: MatDialogRef<IdeaCreateDialogComponent, void>,
    @Inject(MAT_DIALOG_DATA) @Optional() public data?: CreateIdeaDialogInput,
  ) {
    super();
  }

  public get isAnyUploading() {
    return this.attachService.uploadingSize > 0;
  }

  public get formErrors(): number {
    if (!this.isSubmitted) return 0;
    if (this.form.valid) return 0;

    return getFormValidationErrors(this.form).length;
  }

  public get isCompanySelectorDisabled(): boolean {
    const creator = this.form.controls.creator?.value;
    return creator.iportalRole ? false : !!creator?.companyId;
  }

  public ngOnInit() {
    this.createIdea();

    this.attachService.attach$.pipe(takeUntil(this.destroy$)).subscribe((e: AttachEvent) => {
      const formAttaches = this.form.get('attaches')!.value || [];
      this.form.get('attaches')!.setValue([...formAttaches, e.attachment]);
      setTimeout(() => this.scrollToEnd(), 0);
    });

    this.companiesStore.map$.pipe(takeUntil(this.destroy$)).subscribe((companiesMap) => {
      this.companiesMap = companiesMap;
    });
  }

  public close() {
    this.dialogRef.close();
  }

  public submit() {
    this.isSubmitted = true;
    if (this.form.invalid) return;

    this.isSending = true;
    this.saveNewCategory()
      .pipe(
        map((categoryId): Idea => {
          return {
            ...this.newIdea,
            ...this.customFieldsForms.formToIdea(this.form),
            categoryId,
          };
        }),
        switchMap((idea: Idea) => {
          return this.ideasStore.add(idea);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((savedIdea) => {
        this.isSending = false;
        this.newIdea = savedIdea;
      });
  }

  public redirectToIdea() {
    this.close();
    this.router.navigate(['/', this.newIdea.shortId]);
  }

  public addAnotherFeedback() {
    this.createIdea();
  }

  public selectNewCategory(cat: Readonly<Category>) {
    this.newCategory = cat;
  }

  private saveNewCategory() {
    const categoryId = this.form.value.categoryId as string;

    if (this.newCategory && this.newCategory.id === categoryId) {
      return this.categoriesStore
        .add({
          ...this.newCategory,
          id: void 0,
        })
        .pipe(map((category) => category.id));
    } else if (!categoryId) {
      return throwError(() => new Error('categoryId is empty!'));
    }

    return of(categoryId);
  }

  public attachFiles(files: FileList) {
    this.attachService.attachFiles(files, this.newIdea).pipe(takeUntil(this.destroy$)).subscribe();
  }

  private scrollToEnd() {
    const scrollConteiner: HTMLDivElement | null = this.scrollConteinerRef
      ? this.scrollConteinerRef.nativeElement
      : null;

    if (scrollConteiner) {
      scrollConteiner.scrollTop = scrollConteiner.scrollHeight;
    }
  }

  private subscribeToFomValueChanges() {
    this.form.controls.creator.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((creator: CompanyUser | PortalUser) => {
        const companyId = (creator as CompanyUser).companyId;
        this.form.patchValue({
          companyId: (creator as CompanyUser).companyId && !!this.companiesMap[companyId] ? companyId : null,
        });
      });

    this.form.controls.categoryId.valueChanges
      .pipe(
        filter((categoryId) => this.selectedCategoryId !== categoryId),
        switchMap((categoryId) => this.customFieldsForms.buildForm({ ...this.newIdea, categoryId })),
        takeUntil(this.destroy$),
      )
      .subscribe((form) => {
        const oldValues = this.form.value;
        const { custom, categoryId } = form.value;

        const patch = custom.map((value: IdeaCustomFieldValue) => {
          const oldValue = oldValues?.custom?.find((oldValue: IdeaCustomFieldValue) => oldValue.id === value.id);
          return oldValue ? { ...value, value: oldValue.value } : value;
        });

        this.form = form;
        this.selectedCategoryId = categoryId;
        this.form.patchValue({ ...oldValues, categoryId, custom: patch });

        this.subscribeToFomValueChanges();
      });
  }

  private createIdea() {
    this.isSubmitted = false;
    this.buildNewIdea()
      .pipe(
        switchMap((idea) =>
          this.customFieldsForms.buildForm(idea).pipe(
            map((form) => ({ idea, form })), //
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(({ idea, form }) => {
        this.selectedCategoryId = form.controls.categoryId.value;
        this.form = form;
        this.newIdea = idea;

        this.subscribeToFomValueChanges();
      });
  }

  private buildNewIdea(): Observable<Readonly<Idea>> {
    return forkJoin([
      this.categoriesStore.selectedId$.pipe(take(1)), //
      this.importancesStore.list$.pipe(take(1)),
    ]).pipe(
      switchMap(([selectedCategoryId, importances]) => {
        let { importanceId, categoryId } = this.data || ({} as CreateIdeaDialogInput);

        categoryId = categoryId || selectedCategoryId;
        importanceId = importanceId || importances.length > 0 ? importances[0].id : void 0;

        return this.entityFactory.createIdea({
          categoryId,
          importanceId,
        });
      }),
    );
  }
}
